import type { PayoutEntity, PayoutEvent, PayoutStatus } from '../../domain/entities/PayoutEntity.js';
import type { IPayoutRepository, PayoutListOptions } from '../../domain/repositories/IPayoutRepository.js';
import { PayoutRemoteDataSource } from '../datasources/PayoutRemoteDataSource.js';
import type { PayoutRowDto } from '../dtos/PayoutRequestDto.js';

function creatorName(row: PayoutRowDto): string | null {
  const c = row.creator;
  if (!c) return null;
  const full = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  return full || c.email || null;
}

function rowToEvent(row: PayoutRowDto): PayoutEvent {
  return {
    id: row.id,
    requestId: row.id,
    type: row.status as PayoutEvent['type'],
    actorId: row.created_by,
    actorName: creatorName(row),
    at: row.created_at,
    reason: row.reject_reason ?? row.close_reason ?? null,
    paymentReceiptUrl: row.payment_receipt_url,
    rejectedByRole: row.rejected_by_role,
    photoFields: row.photo_fields,
    extractedNames: row.extracted_names,
    matchConfidences: row.match_confidences,
    matchedContactIds: row.matched_contact_ids,
    orgId: row.org_id,
    projectId: row.project_id,
    createdAt: row.created_at,
  };
}

function autoCheckSyntheticEvent(row: PayoutRowDto): PayoutEvent | null {
  if (!row.match_confidences?.length) return null;
  return {
    id: `${row.id}-auto_check`,
    requestId: row.id,
    type: 'auto_check',
    actorId: null,
    actorName: null,
    at: row.created_at,
    reason: null,
    paymentReceiptUrl: null,
    rejectedByRole: null,
    photoFields: row.photo_fields,
    extractedNames: row.extracted_names,
    matchConfidences: row.match_confidences,
    matchedContactIds: row.matched_contact_ids,
    orgId: row.org_id,
    projectId: row.project_id,
    createdAt: row.created_at,
  };
}

function buildEntity(rows: PayoutRowDto[]): PayoutEntity | null {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at));
  const latest = sorted[sorted.length - 1]!;
  const events: PayoutEvent[] = [];
  for (const r of sorted) {
    events.push(rowToEvent(r));
    const ac = autoCheckSyntheticEvent(r);
    if (ac) events.push(ac);
  }
  return {
    id: latest.id,
    sessionId: latest.session_id,
    locationId: latest.location_id,
    contactId: latest.contact_id ?? null,
    formxSubmissionId: latest.formx_submission_id,
    currentStatus: latest.status as PayoutStatus,
    orgId: latest.org_id,
    projectId: latest.project_id,
    createdAt: latest.created_at,
    events,
    contact: latest.owner
      ? {
          id: latest.owner.id,
          fullName: latest.owner.full_name,
          bankAccounts: (latest.owner.bank_accounts ?? []).map((b: any) => ({
            bank: b.bank,
            accountNumber: b.account_number,
            holder: b.holder,
            primary: !!b.primary,
          })),
        }
      : null,
    location: latest.location
      ? {
          id: latest.location.id,
          name: latest.location.name,
          code: latest.location.code,
          addressLine: latest.location.address_line,
          provinceCode: latest.location.province_code,
          provinceName: latest.location.province?.name ?? null,
        }
      : null,
  };
}

export class PayoutRepository implements IPayoutRepository {
  constructor(private readonly ds: PayoutRemoteDataSource) {}

  async list(options: PayoutListOptions) {
    const { data, total } = await this.ds.list(options);
    const sessionIds = data.map((row) => row.session_id);
    if (sessionIds.length === 0) {
      return { data: [], total: 0 };
    }
    const allRows = await this.ds.getChainsBySessionIds(sessionIds);
    const rowsBySessionId = new Map<string, PayoutRowDto[]>();
    for (const r of allRows) {
      if (!rowsBySessionId.has(r.session_id)) {
        rowsBySessionId.set(r.session_id, []);
      }
      rowsBySessionId.get(r.session_id)!.push(r);
    }
    const mapped = data
      .map((latestRow) => {
        const chainRows = rowsBySessionId.get(latestRow.session_id) ?? [latestRow];
        return buildEntity(chainRows)!;
      })
      .filter(Boolean);
    return { data: mapped, total };
  }

  async listRaw(options: PayoutListOptions) {
    const { data, total } = await this.ds.listRaw(options);
    return { data: data.map((row) => buildEntity([row])!).filter(Boolean), total };
  }

  async getBySessionId(sessionId: string) {
    const rows = await this.ds.getChainBySessionId(sessionId);
    return buildEntity(rows);
  }

  async submitEvent(input: { sessionId: string; eventType: 'validated' | 'rejected' | 'paid'; payload?: { reason?: string; paymentReceiptUrl?: string } }) {
    const payload: Record<string, unknown> = {};
    if (input.payload?.reason !== undefined) payload.reason = input.payload.reason;
    if (input.payload?.paymentReceiptUrl !== undefined) payload.payment_receipt_url = input.payload.paymentReceiptUrl;
    const row = await this.ds.submitEvent(input.sessionId, input.eventType, payload);
    if (!row) return null;
    // Return the updated chain so callers can refresh history.
    return this.getBySessionId(row.session_id);
  }

  async closeRequest(sessionId: string, reason: string) {
    const row = await this.ds.closeRequest(sessionId, reason);
    if (!row) return null;
    return this.getBySessionId(row.session_id);
  }

  async createRequest(sessionId: string, formxSubmissionId: string | null) {
    const row = await this.ds.createRequest(sessionId, formxSubmissionId);
    if (!row) return null;
    return this.getBySessionId(row.session_id);
  }

  async uploadReceipt(args: Parameters<IPayoutRepository['uploadReceipt']>[0]): Promise<string> {
    return this.ds.uploadReceipt(args);
  }

  createReceiptUploadTarget(args: Parameters<IPayoutRepository['createReceiptUploadTarget']>[0]) {
    return this.ds.createReceiptUploadTarget(args);
  }

  uploadReceiptToSignedUrl(args: Parameters<IPayoutRepository['uploadReceiptToSignedUrl']>[0]) {
    return this.ds.uploadReceiptToSignedUrl(args);
  }

  getReceiptUrlIfUploaded(bucket: string, path: string) {
    return this.ds.getReceiptUrlIfUploaded(bucket, path);
  }

  subscribe(filter: Parameters<IPayoutRepository['subscribe']>[0], onChange: (row: PayoutEntity | null) => void) {
    return this.ds.subscribe(filter, (row) => {
      if (!row?.session_id) { onChange(null); return; }
      // On any INSERT, refetch the chain — INSERT only changes "latest".
      this.getBySessionId(row.session_id).then(onChange).catch(() => onChange(null));
    });
  }

  async invokeVerify(payoutId: string) {
    return this.ds.invokeVerify(payoutId);
  }

  async canCloseSession(sessionId: string) {
    return this.ds.canCloseSession(sessionId);
  }

  async adminCreate(input: { orgId: string; projectId: string; locationId: string }) {
    const row = await this.ds.adminCreate(input);
    if (!row) return null;
    return this.getBySessionId(row.session_id);
  }

  async adminDeleteBySession(sessionId: string) {
    return this.ds.adminDeleteBySession(sessionId);
  }
}
